import * as React from 'react';
import { ChevronDown } from 'lucide-react';
import { Text, Icon } from '../../display';
import { surfaceVariantClasses } from '../../shared/styleTokens';

/**
 * Card variant types
 * - default: Shadow shadow-sm with 1px border
 * - elevated: Stronger shadow (shadow-md), no border
 * - outlined: No shadow, 1px border
 * - ghost: No shadow, no border, no background
 */
export type CardVariant = 'default' | 'elevated' | 'outlined' | 'ghost';

/**
 * Card padding sizes
 * - none: No padding
 * - sm: 12px padding
 * - md: 20px padding (default)
 * - lg: 28px padding
 */
export type CardPadding = 'none' | 'sm' | 'md' | 'lg';

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Visual variant of the card
   * @default 'default'
   */
  variant?: CardVariant;
  /**
   * Padding size for the card content
   * @default 'md'
   */
  padding?: CardPadding;
  /**
   * Card content
   */
  children: React.ReactNode;
}

export interface CardHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Title text displayed in the header
   */
  title?: string;
  /**
   * Subtitle text displayed below the title
   */
  subtitle?: string;
  /**
   * Action element displayed on the right side of the header
   */
  action?: React.ReactNode;
  /**
   * Whether the card content can be collapsed
   * @default false
   */
  collapsible?: boolean;
  /**
   * Whether the card is expanded by default (only applies when collapsible is true)
   * @default true
   */
  defaultExpanded?: boolean;
  /**
   * Callback when the expanded state changes
   */
  onExpandChange?: (expanded: boolean) => void;
  /**
   * Header content (alternative to title/subtitle)
   */
  children?: React.ReactNode;
}

export interface CardContentProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Content of the card body
   */
  children?: React.ReactNode;
}

export interface CardFooterProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Footer content
   */
  children?: React.ReactNode;
}

/**
 * Context for sharing collapse state between Card sub-components
 */
interface CardContextValue {
  expanded: boolean;
  setExpanded: (expanded: boolean) => void;
  collapsible: boolean;
  onExpandChange?: (expanded: boolean) => void;
}

const CardContext = React.createContext<CardContextValue | null>(null);

/**
 * Hook to access Card context
 */
function useCardContext() {
  return React.useContext(CardContext);
}

/**
 * Variant classes mapping (shared with Panel)
 */
const variantClasses: Record<CardVariant, string> = surfaceVariantClasses;

/**
 * Padding classes mapping for content
 */
const paddingClasses: Record<CardPadding, string> = {
  none: '',
  sm: 'p-3',
  md: 'p-5',
  lg: 'p-7',
};

/**
 * Card root component
 *
 * A container component with elevated background, border, and optional shadow.
 * Use with Card.Header, Card.Content, and Card.Footer sub-components.
 *
 * @example
 * // Basic card
 * <Card>
 *   <Card.Content>Content here</Card.Content>
 * </Card>
 *
 * @example
 * // Card with header and footer
 * <Card>
 *   <Card.Header title="Card Title" subtitle="Optional subtitle" />
 *   <Card.Content>Main content</Card.Content>
 *   <Card.Footer>Footer actions</Card.Footer>
 * </Card>
 *
 * @example
 * // Collapsible card
 * <Card>
 *   <Card.Header title="Collapsible Card" collapsible defaultExpanded={false} />
 *   <Card.Content>Collapsible content</Card.Content>
 * </Card>
 */
const CardRoot = React.forwardRef<HTMLDivElement, CardProps>(
  ({ variant = 'default', padding = 'md', className = '', children, ...props }, ref) => {
    const [expanded, setExpanded] = React.useState(true);
    const [collapsible, setCollapsible] = React.useState(false);
    const [onExpandChange, setOnExpandChange] = React.useState<
      ((expanded: boolean) => void) | undefined
    >(undefined);

    // Extract collapsible settings from CardHeader if present.
    //
    // A *layout* effect, not a passive one: `expanded` is seeded `true` above,
    // so a card whose header asks for `defaultExpanded={false}` stays expanded
    // until this effect applies the header's value. A passive effect runs after
    // paint, so such a card would paint open for one frame and then fold —
    // visible jank on a column of collapsed cards. A layout effect flushes
    // before paint, so the header's value lands in the same visual frame as the
    // mount. The final state is identical either way; only *when* it is applied
    // changes.
    React.useLayoutEffect(() => {
      React.Children.forEach(children, (child) => {
        if (React.isValidElement(child) && child.type === CardHeader) {
          const headerProps = child.props as CardHeaderProps;
          if (headerProps.collapsible !== undefined) {
            setCollapsible(headerProps.collapsible);
          }
          if (headerProps.defaultExpanded !== undefined) {
            setExpanded(headerProps.defaultExpanded);
          }
          if (headerProps.onExpandChange !== undefined) {
            setOnExpandChange(() => headerProps.onExpandChange);
          }
        }
      });
    }, [children]);

    const contextValue: CardContextValue = React.useMemo(
      () => ({
        expanded,
        setExpanded,
        collapsible,
        onExpandChange,
      }),
      [expanded, setExpanded, collapsible, onExpandChange]
    );

    // Pass padding to content children
    const childrenWithPadding = React.Children.map(children, (child) => {
      if (React.isValidElement(child) && child.type === CardContent) {
        return React.cloneElement(child as React.ReactElement<CardContentProps & { _padding?: CardPadding }>, {
          _padding: padding,
        });
      }
      return child;
    });

    const combinedClasses = [
      'rounded-lg',
      variantClasses[variant],
      className,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <CardContext.Provider value={contextValue}>
        <article ref={ref} className={combinedClasses} {...props}>
          {childrenWithPadding}
        </article>
      </CardContext.Provider>
    );
  }
);

CardRoot.displayName = 'Card';

/**
 * Card header component
 *
 * Displays a title, optional subtitle, and optional action element.
 * Can be made collapsible to show/hide card content.
 */
/**
 * Helper to build header CSS classes
 */
function buildHeaderClasses(collapsible: boolean, className: string): string {
  return [
    'px-5 py-4 border-b border-secondary-200',
    collapsible ? 'cursor-pointer select-none' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');
}

/**
 * Helper to get collapsible header props
 */
function getCollapsibleProps(
  collapsible: boolean,
  expanded: boolean,
  handleToggle: () => void,
  handleKeyDown: (e: React.KeyboardEvent) => void
) {
  if (!collapsible) {
    return {};
  }
  return {
    onClick: handleToggle,
    onKeyDown: handleKeyDown,
    role: 'button' as const,
    tabIndex: 0,
    'aria-expanded': expanded,
  };
}

/**
 * Chevron icon for collapsible headers
 */
function CollapsibleChevron({ expanded, shrink = false }: Readonly<{ expanded: boolean; shrink?: boolean }>) {
  return (
    <span
      className={`transition-transform duration-200 ease-out${shrink ? ' flex-shrink-0' : ''}`}
      style={{ transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)' }}
    >
      <Icon icon={ChevronDown} size="sm" />
    </span>
  );
}

/**
 * Action container that stops event propagation.
 * Uses role="toolbar" since it groups action controls, and toolbar is an
 * interactive widget role that legitimately accepts event handlers.
 * tabIndex={-1} makes it focusable programmatically without adding it to tab order.
 */
function ActionContainer({ action, className }: Readonly<{ action: React.ReactNode; className?: string }>) {
  return (
    <div
      role="toolbar"
      tabIndex={-1}
      className={className || 'ml-4'}
      onClick={(e) => { e.stopPropagation(); }}
      onKeyDown={(e) => { e.stopPropagation(); }}
    >
      {action}
    </div>
  );
}

/**
 * Card header component
 *
 * Displays a title, optional subtitle, and optional action element.
 * Can be made collapsible to show/hide card content.
 */
const CardHeader = React.forwardRef<HTMLDivElement, CardHeaderProps>(
  (
    {
      title,
      subtitle,
      action,
      collapsible = false,
      // Note: defaultExpanded and onExpandChange are read by CardRoot via props inspection
      defaultExpanded: _defaultExpanded = true,
      onExpandChange: _onExpandChange,
      className = '',
      children,
      ...props
    },
    ref
  ) => {
    const context = useCardContext();
    const expanded = context?.expanded ?? true;
    const setExpanded = context?.setExpanded;
    const onExpandChange = context?.onExpandChange;

    const handleToggle = () => {
      if (!collapsible || !setExpanded) {
        return;
      }
      const newExpanded = !expanded;
      setExpanded(newExpanded);
      onExpandChange?.(newExpanded);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (collapsible && (e.key === 'Enter' || e.key === ' ')) {
        e.preventDefault();
        handleToggle();
      }
    };

    const combinedClasses = buildHeaderClasses(collapsible, className);
    const collapsibleProps = getCollapsibleProps(collapsible, expanded, handleToggle, handleKeyDown);

    // Render custom children if provided
    if (children) {
      return (
        <header ref={ref} className={combinedClasses} {...collapsibleProps} {...props}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 flex-1">
              {collapsible && <CollapsibleChevron expanded={expanded} />}
              {children}
            </div>
            {action && <ActionContainer action={action} />}
          </div>
        </header>
      );
    }

    return (
      <header ref={ref} className={combinedClasses} {...collapsibleProps} {...props}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            {collapsible && <CollapsibleChevron expanded={expanded} shrink />}
            <div className="min-w-0 flex-1">
              {title && (
                <Text as="h3" size="lg" weight="semibold">
                  {title}
                </Text>
              )}
              {subtitle && (
                <Text size="sm" color="muted" className="mt-0.5">
                  {subtitle}
                </Text>
              )}
            </div>
          </div>
          {action && <ActionContainer action={action} className="ml-4 flex-shrink-0" />}
        </div>
      </header>
    );
  }
);

CardHeader.displayName = 'Card.Header';

/**
 * Card content component
 *
 * The main content area of the card.
 */
const CardContent = React.forwardRef<
  HTMLDivElement,
  CardContentProps & { _padding?: CardPadding }
>(({ className = '', children, _padding = 'md', ...props }, ref) => {
  const context = useCardContext();
  const expanded = context?.expanded ?? true;
  const collapsible = context?.collapsible ?? false;

  const combinedClasses = [paddingClasses[_padding], className]
    .filter(Boolean)
    .join(' ');

  // Apply collapse animation
  if (collapsible) {
    return (
      <div
        className={`${expanded ? 'overflow-visible' : 'overflow-hidden'} transition-all duration-200 ease-out`}
        style={{
          maxHeight: expanded ? '2000px' : '0',
          opacity: expanded ? 1 : 0,
        }}
      >
        <section ref={ref} className={combinedClasses} {...props}>
          {children}
        </section>
      </div>
    );
  }

  return (
    <section ref={ref} className={combinedClasses} {...props}>
      {children}
    </section>
  );
});

CardContent.displayName = 'Card.Content';

/**
 * Card footer component
 *
 * A footer area for actions or additional information.
 */
const CardFooter = React.forwardRef<HTMLDivElement, CardFooterProps>(
  ({ className = '', children, ...props }, ref) => {
    const context = useCardContext();
    const expanded = context?.expanded ?? true;
    const collapsible = context?.collapsible ?? false;

    const combinedClasses = [
      'px-5 py-4 border-t border-secondary-200',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    // Apply collapse animation to footer as well
    if (collapsible) {
      return (
        <div
          className="overflow-hidden transition-all duration-200 ease-out"
          style={{
            maxHeight: expanded ? '200px' : '0',
            opacity: expanded ? 1 : 0,
          }}
        >
          <footer ref={ref} className={combinedClasses} {...props}>
            {children}
          </footer>
        </div>
      );
    }

    return (
      <footer ref={ref} className={combinedClasses} {...props}>
        {children}
      </footer>
    );
  }
);

CardFooter.displayName = 'Card.Footer';

/**
 * Card compound component
 *
 * Usage:
 * - Card.Header - Header with title, subtitle, and optional action
 * - Card.Content - Main content area
 * - Card.Footer - Footer for actions
 */
const Card = Object.assign(CardRoot, {
  Header: CardHeader,
  Content: CardContent,
  Footer: CardFooter,
});

export { Card, CardHeader, CardContent, CardFooter };
